import api from "GlobalAPI";
import { type Ref, ref } from "vue";

export type TCityOption = {
  choice: number | string;
  label: string;
  slug: string;
};

export const LOCATION_ERROR = {
  Denied: "denied",
  Failed: "failed",
  NoMatch: "no-match",
  Unsupported: "unsupported",
} as const;

export type TLocationError =
  (typeof LOCATION_ERROR)[keyof typeof LOCATION_ERROR];

const GEOLOCATION_TIMEOUT = 15_000;

/** GeolocationPositionError.PERMISSION_DENIED */
const PERMISSION_DENIED = 1;

function isPermissionDenied(thrown: unknown): boolean {
  return (thrown as GeolocationPositionError)?.code === PERMISSION_DENIED;
}

function currentPosition(): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      maximumAge: 0,
      timeout: GEOLOCATION_TIMEOUT,
    });
  });
}

/**
 * Turns the visitor's device position into a city option shaped like the ones
 * CitySelect emits, so both paths feed the same selection handler.
 */
export function useCurrentLocation() {
  const isLocating = ref(false);
  const error: Ref<TLocationError | null> = ref(null);

  async function locate(): Promise<TCityOption | null> {
    if (!navigator.geolocation) {
      error.value = LOCATION_ERROR.Unsupported;
      return null;
    }

    isLocating.value = true;
    error.value = null;

    try {
      const { latitude, longitude } = (await currentPosition()).coords;
      const { cityId, cityName, citySlug } =
        (await api.get(
          `get-city-from-long-lat?lat=${latitude}&long=${longitude}`
        )) ?? {};

      if (!cityId) {
        error.value = LOCATION_ERROR.NoMatch;
        return null;
      }

      return { choice: cityId, label: cityName, slug: citySlug };
    } catch (thrown) {
      error.value = isPermissionDenied(thrown)
        ? LOCATION_ERROR.Denied
        : LOCATION_ERROR.Failed;
      return null;
    } finally {
      isLocating.value = false;
    }
  }

  return { error, isLocating, locate };
}
